import { useCallback, useMemo } from 'react';
import { LOCALES, translate, useLocaleStore, type Locale, type TranslateParams } from './store';
import type { TranslationKey } from './locales/fr';

export type TFunction = (key: TranslationKey, params?: TranslateParams) => string;

/**
 * Hook de traduction : `const { t, locale, dir, formatDate } = useT()`.
 * Se met à jour automatiquement quand la langue change.
 */
export function useT() {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const meta = LOCALES.find((l) => l.code === locale)!;

  const t = useCallback<TFunction>((key, params) => translate(locale, key, params), [locale]);

  const fmt = useMemo(() => {
    const intl = meta.intl;
    return {
      formatNumber: (n: number, opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat(intl, opts).format(n),
      formatCurrency: (n: number, currency = 'EUR') =>
        new Intl.NumberFormat(intl, { style: 'currency', currency }).format(n),
      formatDate: (d: string | number | Date, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) =>
        new Intl.DateTimeFormat(intl, opts).format(new Date(d)),
      formatDateTime: (d: string | number | Date) =>
        new Intl.DateTimeFormat(intl, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(d)),
      formatTime: (d: string | number | Date) =>
        new Intl.DateTimeFormat(intl, { hour: '2-digit', minute: '2-digit' }).format(new Date(d)),
      /** « il y a 5 min », « in 2 h »… */
      formatRelative: (d: string | number | Date, now = Date.now()) => {
        const diffSec = Math.round((new Date(d).getTime() - now) / 1000);
        const rtf = new Intl.RelativeTimeFormat(intl, { numeric: 'auto' });
        const abs = Math.abs(diffSec);
        if (abs < 60) return rtf.format(diffSec, 'second');
        if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
        if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
        return rtf.format(Math.round(diffSec / 86400), 'day');
      },
      /** Durée en secondes -> « 1 h 05 » / « 12 min ». */
      formatDuration: (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const nf = new Intl.NumberFormat(intl, { minimumIntegerDigits: 2 });
        if (h > 0) return `${h} h ${nf.format(m)}`;
        return `${m} min`;
      },
    };
  }, [meta.intl]);

  return { t, locale: locale as Locale, setLocale, dir: meta.dir, ...fmt };
}
